// SPDX-License-Identifier: AGPL-3.0-or-later
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import type {
	AssistantConnectorDefinition,
	AssistantConnectorView,
	ConnectorAccess,
	ConnectorMemberView,
} from '@konvo/schema/src/domains/konvo/AssistantConnector';
import type {AssistantFlowRunView, AssistantFlowView} from '@konvo/schema/src/domains/konvo/AssistantFlow';
import {makeAutoObservable, runInAction} from 'mobx';

const base = '/konvo/assistant';
export type ConnectorMember = ConnectorMemberView;
/** The API's own sentence (`{error}`) when there is one; HttpError.message is only "[400] POST /path". */
export function capabilityError(error: unknown, fallback: string) {
	if (error instanceof HttpError) {
		const body = error.body as {error?: unknown} | null | undefined;
		if (typeof body?.error === 'string' && body.error) return body.error;
		return fallback;
	}
	return error instanceof Error && error.message ? error.message : fallback;
}
class AssistantCapabilities {
	connectors: Array<AssistantConnectorView> = [];
	flows: Array<AssistantFlowView> = [];
	runs: Array<AssistantFlowRunView> = [];
	canConfigure = false;
	oauthCallbackUrl = '';
	/** The Konvo GitHub App's page when the server signs people in with it. */
	githubAppUrl: string | null = null;
	/** Staff: people who can be added to a connector's access grid. */
	members: Array<ConnectorMember> = [];
	loading = false;
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}
	async refreshConnectors() {
		const response = await http.get<{
			connectors: Array<AssistantConnectorView>;
			can_configure: boolean;
			oauth_callback_url?: string;
			github_app_url?: string | null;
		}>(`${base}/connectors`);
		runInAction(() => {
			this.connectors = response.body.connectors;
			this.canConfigure = response.body.can_configure;
			this.oauthCallbackUrl = response.body.oauth_callback_url ?? '';
			this.githubAppUrl = response.body.github_app_url ?? null;
		});
	}
	async refreshFlows() {
		const [flows, runs] = await Promise.all([
			http.get<{flows: Array<AssistantFlowView>}>(`${base}/flows`),
			http.get<{runs: Array<AssistantFlowRunView>}>(`${base}/flows/history`),
		]);
		runInAction(() => {
			this.flows = flows.body.flows;
			this.runs = runs.body.runs;
		});
	}
	/** The built-in GitHub definition, for "Restore GitHub defaults". */
	async githubDefaults() {
		return (await http.get<{definition: AssistantConnectorDefinition}>(`${base}/connectors/preset/github`)).body
			.definition;
	}
	async configure(
		definition: AssistantConnectorDefinition,
		version: number | null,
		client_secret?: string,
		access?: ConnectorAccess,
	) {
		await http.post(`${base}/connectors/configure`, {
			body: {
				definition,
				version,
				...(client_secret === undefined ? {} : {client_secret}),
				...(access === undefined ? {} : {access}),
			},
		});
		await this.refreshConnectors();
	}
	/** Access changes keep the connector version, so approved automations stay approved. */
	async saveAccess(id: string, access: ConnectorAccess) {
		await http.post(`${base}/connectors/${id}/access`, {body: {access}});
		await this.refreshConnectors();
	}
	async refreshMembers() {
		const response = await http.get<{members: Array<ConnectorMember>}>(`${base}/connectors/members`);
		runInAction(() => {
			this.members = response.body.members;
		});
	}
	async token(id: string, token: string) {
		await http.post(`${base}/connectors/${id}/token`, {body: {token}});
		await this.refreshConnectors();
	}
	async remove(id: string) {
		await http.post(`${base}/connectors/${id}/delete`, {body: {}});
		await this.refreshConnectors();
	}
	async disconnect(id: string) {
		await http.post(`${base}/connectors/${id}/disconnect`, {body: {}});
		await this.refreshConnectors();
	}
	async oauth(id: string) {
		return (await http.post<{url: string}>(`${base}/connectors/${id}/oauth`, {body: {}})).body.url;
	}
	async requestFlow(request: string, flow?: AssistantFlowView) {
		const reply = await http.post<{flow?: AssistantFlowView; clarification?: string}>(`${base}/flows/request`, {
			body: {request, ...(flow ? {id: flow.id, version: flow.version} : {})},
			timeoutMs: 180000,
		});
		await this.refreshFlows();
		return reply.body;
	}
	async authorize(flow: AssistantFlowView) {
		await http.post(`${base}/flows/${flow.id}/authorize`, {body: {version: flow.version}});
		await this.refreshFlows();
	}
	async control(flow: AssistantFlowView, operation: 'pause' | 'resume' | 'delete' | 'run') {
		await http.post(`${base}/flows/${flow.id}/control`, {body: {version: flow.version, operation}});
		await this.refreshFlows();
	}
	async preview(flow: AssistantFlowView) {
		return (await http.post<{steps: Array<unknown>}>(`${base}/flows/${flow.id}/preview`, {body: {}, timeoutMs: 180000}))
			.body;
	}
	async approve(run: AssistantFlowRunView, approve: boolean) {
		await http.post(`${base}/flows/runs/${run.id}/approval`, {
			body: {version: run.version, approve, approval_id: run.pending_approval?.id ?? null},
		});
		await this.refreshFlows();
	}
	async cancel(run: AssistantFlowRunView) {
		await http.post(`${base}/flows/runs/${run.id}/cancel`, {body: {}});
		await this.refreshFlows();
	}
}
export default new AssistantCapabilities();
